import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Camera, Loader2, Upload, X } from 'lucide-react';
import { facilityAPI, roomsAPI } from '../../lib/api';
import { CATEGORY_LABELS, URGENCY_LABELS, ORDER_TYPE_LABELS, ORDER_TYPE_ORDER, TYPE_FIELDS, RECURRENCE_LABELS } from './constants';
import { toast } from 'sonner';

const EMPTY = { title: '', description: '', order_type: 'schaden', category: 'sonstiges', urgency: 'medium', location_id: '', room_id: '', details: {} };

export const ReportDialog = ({ open, onOpenChange, locations = [], defaultLocationId = '', onCreated, mode = 'board' }) => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, location_id: defaultLocationId || '' });
      setFiles([]);
    }
  }, [open, defaultLocationId]);

  useEffect(() => {
    if (!form.location_id) { setRooms([]); return; }
    roomsAPI.getAll({ location_id: form.location_id })
      .then((r) => setRooms(r.data || []))
      .catch(() => setRooms([]));
  }, [form.location_id]);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith('image/'));
    setFiles((prev) => [...prev, ...arr].slice(0, 8));
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Bitte einen Titel angeben'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        order_type: form.order_type,
        category: form.category,
        urgency: form.urgency,
        location_id: form.location_id || null,
        room_id: form.room_id || null,
        details: form.details || {},
      };
      const res = mode === 'self' ? await facilityAPI.report(payload) : await facilityAPI.create(payload);
      const ticket = res.data;
      const uploader = mode === 'self' ? facilityAPI.uploadReportPhoto : facilityAPI.uploadPhoto;
      for (const f of files) {
        try { await uploader(ticket.id, f, 'report'); } catch { /* einzelnes Foto fehlgeschlagen */ }
      }
      toast.success(mode === 'self' ? 'Anliegen gemeldet' : 'Auftrag angelegt');
      onOpenChange(false);
      onCreated?.(ticket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Melden');
    } finally {
      setSaving(false);
    }
  };

  const setDetail = (k, v) => setForm((f) => ({ ...f, details: { ...f.details, [k]: v } }));
  const typeFields = TYPE_FIELDS[form.order_type] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="facility-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{mode === 'self' ? 'Anliegen / Schaden melden' : 'Auftrag anlegen'}</DialogTitle>
          <DialogDescription>{mode === 'self' ? 'Melde einen Schaden oder ein Anliegen – die Haustechnik kümmert sich darum.' : 'Neuen Haustechnik-Auftrag erstellen (Schaden, Renovierung, Transport, Besorgung …).'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Auftragstyp</Label>
            <Select value={form.order_type} onValueChange={(v) => setForm((f) => ({ ...f, order_type: v, details: {} }))}>
              <SelectTrigger data-testid="facility-ordertype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORDER_TYPE_ORDER.map((k) => <SelectItem key={k} value={k}>{ORDER_TYPE_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Titel *</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="z. B. Wasserhahn tropft" data-testid="facility-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Beschreibung</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Wo genau? Was ist das Problem?" rows={3} data-testid="facility-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="facility-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dringlichkeit</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm((f) => ({ ...f, urgency: v }))}>
                <SelectTrigger data-testid="facility-urgency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(URGENCY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {typeFields.length > 0 && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/50 p-3 bg-secondary/20">
              {typeFields.map((fld) => (
                <div key={fld.key} className={`space-y-1 ${fld.type === 'textarea' ? 'col-span-2' : ''}`}>
                  <Label className="text-xs">{fld.label}</Label>
                  {fld.type === 'textarea' ? (
                    <Textarea value={form.details[fld.key] || ''} onChange={(e) => setDetail(fld.key, e.target.value)} placeholder={fld.placeholder || ''} rows={2} data-testid={`facility-detail-${fld.key}`} />
                  ) : fld.type === 'select' ? (
                    <Select value={form.details[fld.key] || fld.options[0]} onValueChange={(v) => setDetail(fld.key, v)}>
                      <SelectTrigger data-testid={`facility-detail-${fld.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fld.options.map((o) => <SelectItem key={o} value={o}>{RECURRENCE_LABELS[o] || o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={fld.type} value={form.details[fld.key] || ''} onChange={(e) => setDetail(fld.key, e.target.value)} placeholder={fld.placeholder || ''} data-testid={`facility-detail-${fld.key}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Standort</Label>
              <Select value={form.location_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v === 'none' ? '' : v, room_id: '' }))}>
                <SelectTrigger data-testid="facility-location-select"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">– Kein Standort –</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Raum (optional)</Label>
              <Select value={form.room_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, room_id: v === 'none' ? '' : v }))} disabled={!rooms.length}>
                <SelectTrigger data-testid="facility-room-select"><SelectValue placeholder={rooms.length ? 'Raum wählen' : '–'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">– Kein Raum –</SelectItem>
                  {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Foto-Bereich */}
          <div className="space-y-2">
            <Label>Fotos</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              className={`rounded-xl border-2 border-dashed p-4 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border/60'}`}
              data-testid="facility-photo-dropzone"
            >
              <div className="flex flex-wrap gap-2 justify-center mb-3">
                {files.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 object-cover rounded-lg border" />
                    <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mb-2">Bilder hierher ziehen oder auswählen (max. 8)</p>
              <div className="flex gap-2 justify-center">
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="facility-choose-photo-btn">
                  <Upload className="h-4 w-4 mr-1" /> Datei
                </Button>
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => camRef.current?.click()} data-testid="facility-camera-btn">
                  <Camera className="h-4 w-4 mr-1" /> Kamera
                </Button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => addFiles(e.target.files)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
            <Button className="rounded-full" onClick={submit} disabled={saving} data-testid="facility-submit-report-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Melden'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
